// ConversationBody.tsx - Consolidated WhatsApp-like conversation UI
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ConversationBody.css";
import moment from "moment";
import { Spin } from "antd";
import logger, { Logger } from '../../../utils/logger';
import { findMediaGroups, getMediaGroupId } from '../utils/mediaGroupingUtil';
import MediaGroup from '../utils/MediaGroup';
import { useDropzone } from 'react-dropzone';

// Redux imports
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  fetchMessages,
  selectMessagesByDate,
  selectMessagesStatus,
  Message,
  fetchOlderMessages,
  selectHasMoreMessages,
  selectLoadingOlderMessages
} from "../../../store/slices/messagesSlice";
import {
  selectSelectedConversation
} from "../../../store/slices/conversationsSlice";
import {
  selectMediaCache
} from "../../../store/slices/mediaSlice";

// Component imports
import { DateDivider } from "./index";
import { MessageBubbleUpdated } from "./MessageBubbleUpdated";

// Hook imports
import { useScrollToTop } from "../../../hooks/useScrollToTop";

interface ConversationBodyProps {
  scrollToBottom: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  addFiles?: (files: File[]) => void;  // For drag and drop file upload
}

const ConversationBody: React.FC<ConversationBodyProps> = ({ scrollToBottom, containerRef, addFiles }) => {
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const messagesByDate = useAppSelector(selectMessagesByDate);
  const messagesStatus = useAppSelector(selectMessagesStatus);
  const mediaCache = useAppSelector(selectMediaCache);
  const hasMoreMessages = useAppSelector(selectHasMoreMessages);
  const loadingOlderMessages = useAppSelector(selectLoadingOlderMessages);

  // Local state
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [isMounted, setIsMounted] = useState(true);

  // 🔥 Drag and drop configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      Logger.log('📂 Conversation area drop detected', {
        accepted: acceptedFiles.length,
        rejected: rejectedFiles.length
      });

      // Log rejected files
      rejectedFiles.forEach(rejection => {
        Logger.log('❌ File rejected:', rejection.file.name, rejection.errors);
      });

      // Add accepted files to queue
      if (acceptedFiles.length > 0 && addFiles) {
        addFiles(acceptedFiles);

        if (acceptedFiles.length > 1) {
          logger.log(`🔔 MULTIPLE FILES DROPPED: ${acceptedFiles.length} files`);
        } else {
          logger.log(`📤 SINGLE FILE DROPPED: ${acceptedFiles[0].name}`);
        }
      }
    },
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'video/mp4': ['.mp4'],
      'video/3gpp': ['.3gp'],
      'application/pdf': ['.pdf']
    },
    multiple: true,
    noClick: true,  // Don't open file dialog on click
    noKeyboard: true,
    disabled: !addFiles  // Disable if no addFiles function provided
  });

  // Scroll hook for loading older messages - use containerRef if provided
  const scrollRef = useScrollToTop({
    threshold: 100,
    onScrollToTop: () => {
      if (selectedConversation?.convoId) {
        logger.log('🔄 User scrolled to top, loading older messages...');
        dispatch(fetchOlderMessages(selectedConversation.convoId));
      }
    },
    isLoading: loadingOlderMessages,
    hasMore: hasMoreMessages,
    externalRef: containerRef // Pass parent ref to hook
  });

  // Logging for debugging
  useEffect(() => {
    Logger.log("ConversationBody rendered", {
      conversationId: selectedConversation?.convoId,
      messagesStatus,
      hasMoreMessages,
      loadingOlderMessages
    });

    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, [selectedConversation, messagesStatus, hasMoreMessages, loadingOlderMessages]);

  // Fetch messages when conversation changes
  useEffect(() => {
    if (selectedConversation?.convoId) {
      Logger.log("Fetching messages for conversation:", selectedConversation.convoId);
      dispatch(fetchMessages(selectedConversation.convoId));
    }
  }, [selectedConversation?.convoId, dispatch]);

  // Smart auto-scroll logic
  useEffect(() => {
    const currentMessageCount = Object.keys(messagesByDate).reduce((acc, date) =>
      acc + Object.keys(messagesByDate[date]).reduce((acc2, time) =>
        acc2 + messagesByDate[date][time].length, 0), 0);

    if (currentMessageCount > 0) {
      const countDifference = currentMessageCount - previousMessageCount;

      logger.log('🔥 MESSAGE COUNT CHECK:', {
        previous: previousMessageCount,
        current: currentMessageCount,
        difference: countDifference
      });

      // Only scroll if: first load OR small increase (1-5 new messages = normal chat)
      const shouldScroll = previousMessageCount === 0 || (countDifference > 0 && countDifference <= 5);

      if (shouldScroll) {
        Logger.log("Messages changed, scrolling to bottom", {
          messageCount: currentMessageCount,
          reason: previousMessageCount === 0 ? 'initial_load' : 'new_messages'
        });

        setTimeout(() => {
          scrollToBottom();
        }, 300);
      } else {
        Logger.log("Messages changed, NOT scrolling (older messages loaded)", {
          messageCount: currentMessageCount,
          countDifference
        });
      }

      setPreviousMessageCount(currentMessageCount);
    }
  }, [messagesByDate, scrollToBottom, previousMessageCount]);

  // Check if a message has cached media
  const isMessageMediaCached = useCallback((messageId: string) => {
    return !!mediaCache[messageId];
  }, [mediaCache]);

  // Get cached media for a message
  const getCachedMedia = useCallback((messageId: string) => {
    return mediaCache[messageId] || null;
  }, [mediaCache]);

  // Group messages by time helper
  const groupedByTime = useCallback((messages: any[]) => {
    try {
      return messages.reduce((acc: any, item: any) => {
        const time = moment(item.createdAt).format("hh:mm a");
        if (!acc[time]) {
          acc[time] = [];
        }
        acc[time].push(item);
        return acc;
      }, {});
    } catch (error) {
      Logger.log("Error grouping messages by time:", error);
      return {};
    }
  }, []);

  // Empty chat state
  const renderEmptyChat = useCallback(() => {
    return (
      <div className="conversation-empty">
        <div className="conversation-empty-content">
          <h3>No conversation to show</h3>
          <p>Your inbox is empty. Messages will appear here when they arrive.</p>
        </div>
      </div>
    );
  }, []);

  const renderMessagesContent = useCallback(() => {
    if (JSON.stringify(messagesByDate) === "{}") {
      return renderEmptyChat();
    }

    // Prepare all messages in a flat array and filter out standalone reactions
    const allMessages: Message[] = [];
    let filteredReactionCount = 0;

    Object.keys(messagesByDate).forEach(date => {
      Object.keys(messagesByDate[date]).forEach(time => {
        messagesByDate[date][time].forEach(msg => {
          // Filter out standalone reaction messages
          if (msg.messageType === 'reaction') {
            logger.log('🚫 Filtering out standalone reaction message:', {
              messageId: msg._id,
              messageType: msg.messageType,
              emoji: msg.message?.emoji
            });
            filteredReactionCount++;
            return;
          }

          allMessages.push(msg);
        });
      });
    });

    logger.log('📊 Message filtering summary:', {
      totalMessages: allMessages.length + filteredReactionCount,
      displayedMessages: allMessages.length,
      filteredReactions: filteredReactionCount
    });

    // Find media groups
    const mediaGroups = findMediaGroups(allMessages);
    logger.log("ConversationBody - Detected media groups:", mediaGroups.length);

    // Create a set of message IDs that are part of groups
    const groupedMessageIds = new Set<string>();
    mediaGroups.forEach(group => {
      group.forEach(msg => {
        groupedMessageIds.add(msg._id);
      });
    });

    const renderedGroupIds = new Set<string>();

    return (
      <>
        {/* Loading indicator for older messages at TOP */}
        {loadingOlderMessages && (
          <div className="conversation-loading-older">
            <Spin size="small" />
            <span>Loading older messages...</span>
          </div>
        )}

        {/* "Beginning of conversation" indicator */}
        {!hasMoreMessages && allMessages.length > 0 && (
          <div className="conversation-beginning">
            📅 Beginning of conversation
          </div>
        )}

        {Object.keys(messagesByDate).map((date: string, dateIndex: number) => {
          const timeGroups = groupedByTime(
            Object.keys(messagesByDate[date])
              .flatMap(time => messagesByDate[date][time])
          );

          return (
            <div key={`date-${dateIndex}`} className="conversation-date-group">
              <Spin spinning={messagesStatus === 'loading'} size="large" tip="Loading...">
                <DateDivider date={date} />

                {Object.keys(timeGroups).map((timeKey, timeIndex) => {
                  const processedMessages: ReactNode[] = [];
                  let skipCounter = 0;

                  timeGroups[timeKey].forEach((message: Message, msgIndex: number) => {
                    // If this message is part of a group and not the first one, skip it
                    if (skipCounter > 0) {
                      skipCounter--;
                      return;
                    }

                    // Check if this message starts a group
                    if (groupedMessageIds.has(message._id)) {
                      const group = mediaGroups.find(g => g.some(m => m._id === message._id));

                      if (group) {
                        const groupId = getMediaGroupId(group);

                        if (!renderedGroupIds.has(groupId)) {
                          renderedGroupIds.add(groupId);
                          logger.log("Adding media group:", groupId, "with", group.length, "items");

                          processedMessages.push(
                            <div key={groupId} className="conversation-message-wrapper">
                              <MediaGroup messages={group} />
                            </div>
                          );
                        }

                        skipCounter = group.length - 1;
                        return;
                      }
                    }

                    // Regular message (not in a group)
                    const uniqueKey = `${
                      message._id ||
                      `${Date.now()}-${message.createdAt}-${msgIndex}`
                    }`;

                    processedMessages.push(
                      <MessageBubbleUpdated
                        key={uniqueKey}
                        message={message}
                        isFirstInGroup={msgIndex === 0}
                        isLastInGroup={msgIndex === timeGroups[timeKey].length - 1}
                        showTimestamp={true}
                      />
                    );
                  });

                  return (
                    <div key={`time-${dateIndex}-${timeIndex}`} className="conversation-time-group">
                      {processedMessages}
                    </div>
                  );
                })}
              </Spin>
            </div>
          );
        })}
      </>
    );
  }, [
    messagesByDate,
    messagesStatus,
    groupedByTime,
    renderEmptyChat,
    loadingOlderMessages,
    hasMoreMessages
  ]);

  return (
    <div {...getRootProps({ className: 'conversation-dropzone' })} style={{ position: 'relative', height: '100%', width: '100%' }}>
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(63, 174, 253, 0.1)',
          border: '3px dashed #3FAEFD',
          borderRadius: '16px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            animation: 'bounce 0.5s ease infinite alternate'
          }}>
            📤
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#3FAEFD',
            marginBottom: '8px',
            fontFamily: 'Gilroy-Medium'
          }}>
            Drop files here to upload
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6B7280',
            fontFamily: 'Gilroy-Medium'
          }}>
            Supports images, videos, and PDFs
          </div>
        </div>
      )}

      {renderMessagesContent()}
      <div className="conversation-body-spacer" />
    </div>
  );
};

export default ConversationBody;
